import React, { useState, useEffect, useRef } from 'react';
import styles from './PAwesomePets.module.css';
import YouMayAlsoLike from '../components/YouMayAlsoLike';

const PAwesomePets = ({ onNavigate }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const pageRef = useRef(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // Pet images for carousel
  const petImages = [
    { src: '/website_images/dog_friendly_treats1.jpg', alt: 'Additional dog treats and amenities' },
    { src: '/website_images/dog_friendly_treats.jpg', alt: 'Dog treats available at the bar' },
    { src: '/website_images/dog_friendly_notice.jpg', alt: 'Dog-friendly information and policies' }
  ];

  // Dog amenities and facilities
  const dogAmenities = [
    'Fresh water bowls available on request',
    'Complimentary dog treats at the bar',
    'Poop bags provided',
    'Secure rear garden area',
    'Shaded areas in the garden for hot days',
    'Dog-friendly bar and lounge areas'
  ];

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === petImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? petImages.length - 1 : prevIndex - 1
    );
  };

  return (
    <div ref={pageRef} className={styles['pawsome-pets-page']}>
      <div className={styles.container}>
        <h1 className={styles['page-title']}>P-awesome Pets</h1>
        
        {/* Introduction Section */}
        <div className={styles['intro-section']}>
          <p>
            As passionate dog lovers ourselves, we understand that your four-legged family members are just as important as any other guest. 
            We currently share our home above the hotel with three wonderful dogs - Rocco, Roxy, and Rosie - so we know exactly what makes 
            both dogs and their owners happy during their stay.
          </p>
          <p>
            Our hotel and bar areas have been designed with dogs in mind, ensuring your beloved pets feel as welcome and comfortable as you do. 
            From the moment you arrive, you'll find we've thought of everything to make your dog's stay memorable for all the right reasons.
          </p>
        </div>

        {/* Image Carousel */}
        {petImages.length > 0 && (
          <div className={styles['carousel-container']} data-testid="image-carousel">
            <button className={styles['carousel-btn']} onClick={prevImage} aria-label="Previous image">
              ‹
            </button>
            <div className={styles.carousel}>
              <img 
                src={petImages[currentImageIndex].src}
                alt={petImages[currentImageIndex].alt}
                className={styles['carousel-image']}
              />
            </div>
            <button className={styles['carousel-btn']} onClick={nextImage} aria-label="Next image">
              ›
            </button>
          </div>
        )}

        {/* Dog-Friendly Areas */}
        <div className={styles['areas-section']}>
          <h2 className={styles['section-title']}>Dog-Friendly Areas</h2>
          <div className={styles['areas-grid']}>
            <div className={styles['area-card']}>
              <h3>Bar & Lounge</h3>
              <p>
                Our bar and lounge areas are 100% dog-friendly with no restrictions on where your dog can go. 
                We simply ask that you make responsible decisions based on your dog's size and temperament, 
                keeping in mind other guests who may be nervous around dogs.
              </p>
            </div>
            <div className={styles['area-card']}>
              <h3>Rear Garden</h3>
              <p>
                Our secure rear garden can be fully enclosed by closing both entrance gates, creating a safe space 
                for dogs to explore. High hedges provide lovely shaded areas to keep your pooch cool during warm weather.
              </p>
            </div>
            <div className={styles['area-card']}>
              <h3>Front Garden</h3>
              <p>
                While our front garden is accessible, please note it has access to a main road. We recommend keeping 
                your dog on a lead in this area for their safety.
              </p>
            </div>
          </div>
        </div>

        {/* Hotel Room Policies */}
        <div className={styles['policies-section']}>
          <h2 className={styles['section-title']}>Hotel Room Policies</h2>
          <div className={styles['policies-content']}>
            <div className={styles['policy-card']}>
              <h3>Dog-Friendly Rooms</h3>
              <p>
                Of our 4 beautiful rooms, 2 are designated as dog-friendly (Room 1 and Room 4). These rooms can be easily 
                identified on our Cosy Stays page where each room card clearly shows whether pets are welcome.
              </p>
              <p>
                <strong>Important:</strong> It is your responsibility to ensure you book a dog-friendly room when travelling with pets. 
                Please double-check your booking before arrival.
              </p>
              <div className={styles['price-info']}>
                <strong>£10 per dog, per night surcharge</strong>
                <span className={styles['price-note']}>(payable directly at the hotel)</span>
              </div>
            </div>
            <div className={styles['policy-card']}>
              <h3>Pet-Free Rooms</h3>
              <p>
                Our other 2 rooms (Room 5 and Room 6) are kept 100% pet-free to accommodate guests with allergies. 
                Please note that connecting halls, stairs, and common areas are used by both guest dogs and our resident dogs.
              </p>
              <p>
                <strong>Strict Policy:</strong> Dogs will not be allowed in pet-free rooms under any circumstances to maintain 
                our allergy-friendly environment for sensitive guests.
              </p>
            </div>
          </div>
        </div>

        {/* House Rules */}
        <div className={styles['rules-section']}>
          <h2 className={styles['section-title']}>Our Friendly House Rules</h2>
          <div className={styles['rules-content']}>
            <ul className={styles['rules-list']}>
              <li>All dogs must be kept on a lead and under control at all times</li>
              <li>Please be considerate of other guests who may be anxious around dogs</li>
              <li>Dogs causing excessive disruption or noise may be asked to be taken outside</li>
              <li>Owners are responsible for cleaning up after their pets</li>
              <li>We reserve the right to refuse entry to aggressive or uncontrolled dogs</li>
            </ul>
            <p className={styles['rules-note']}>
              These simple guidelines help ensure everyone - both two-legged and four-legged guests - can enjoy their time with us.
            </p>
          </div>
        </div>

        {/* Dog Amenities */}
        <div className={styles['amenities-section']}>
          <h2 className={styles['section-title']}>What We Provide</h2>
          <div className={styles['amenities-grid']}>
            {dogAmenities.map((amenity, index) => (
              <div key={index} className={styles['amenity-item']}>
                <span className={styles['amenity-text']}>{amenity}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Harlech Beach Information */}
        <div className={styles['beach-section']}>
          <h2 className={styles['section-title']}>Harlech Beach - Dog Information</h2>
          <div className={styles['beach-grid']}>
            <div className={styles['beach-card']}>
              <h3>Summer Season (1st April - 30th September)</h3>
              <p>
                Dogs are permitted <strong>only on the left-side of the main Beach Road entrance</strong>. 
                This seasonal restriction helps protect wildlife and ensures everyone can enjoy the beach.
              </p>
            </div>
            <div className={styles['beach-card']}>
              <h3>Winter Season (1st October - 31st March)</h3>
              <p>
                Dogs are welcome <strong>anywhere on the beach</strong> during these months. Perfect for those 
                energetic winter walks and letting your dog run free on the sand.
              </p>
            </div>
            <div className={styles['beach-card']}>
              <h3>Important Beach Safety</h3>
              <ul className={styles['safety-list']}>
                <li><strong>Sand Dunes:</strong> Generally open but watch for hidden wire fences around perimeters</li>
                <li><strong>Waste Disposal:</strong> No bins on beach - take waste back to Beach Road car park bins</li>
                <li><strong>Jellyfish:</strong> Sometimes wash ashore (mostly harmless but keep dogs away)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Harlech Castle Information */}
        <div className={styles['castle-section']}>
          <h2 className={styles['section-title']}>Harlech Castle - Dog-Friendly Visits</h2>
          <div className={styles['castle-content']}>
            <div className={styles['castle-info']}>
              <h3>Castle Grounds</h3>
              <p>
                Harlech Castle welcomes well-behaved dogs throughout the historic grounds. Explore this UNESCO World Heritage 
                site with your four-legged companion and enjoy the spectacular views over Cardigan Bay.
              </p>
            </div>
            <div className={styles['castle-info']}>
              <h3>Castle Café</h3>
              <p>
                While dogs aren't permitted inside the café, there's a lovely <strong>outdoor terrace where dogs are welcome</strong>. 
                Perfect for a refreshing break with stunning castle views after exploring the grounds.
              </p>
            </div>
          </div>
        </div>

        {/* Local Walks & Recommendations */}
        <div className={styles['walks-section']}>
          <h2 className={styles['section-title']}>Local Dog Walks & Recommendations</h2>
          <div className={styles['walks-content']}>
            <p>
              Our knowledgeable staff are always happy to provide recommendations for local dog walks and adventures. 
              From scenic coastal paths to mountain trails, the Harlech area offers endless opportunities for you and your 
              four-legged friend to explore together.
            </p>
            <p>
              <a 
                href="https://www.alltrails.com/explore?b_br_lat=52.77346999999992&b_br_lng=-3.827336725131147&b_tl_lat=52.94722999999965&b_tl_lng=-4.383643274869428"
                target="_blank"
                rel="noopener noreferrer"
                className={styles['walks-link']}
              >
                Explore Dog-Friendly Walking Trails in the Harlech Area
              </a>
            </p>
          </div>
        </div>

        {/* Contact Section */}
        <div className={styles['contact-section']}>
          <h2 className={styles['section-title']}>Questions About Bringing Your Dog?</h2>
          <p>
            If you have any questions about our dog-friendly facilities, room availability, local walks, or would like to 
            discuss your pet's specific needs, please don't hesitate to contact us before your arrival. We're always happy 
            to help ensure your stay is perfect for both you and your four-legged companion.
          </p>
        </div>
      </div>
      <YouMayAlsoLike currentPage="pawsome-pets" onNavigate={onNavigate} />
    </div>
  );
};

export default PAwesomePets;